import { ExtranetOrderDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacCheckbox,
  PharmacColumn,
  PharmacH6,
  PharmacIcon,
  PharmacRowContainer,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useNavigate } from 'react-router-dom'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { formatBirthdate } from '../../utils/formatters'

type OrderHistoryCustomerItemProps = {
  checked: boolean
  handleOrderCheckChange: () => void
  item: ExtranetOrderDto
}

export const OrderHistoryCustomerGroupOrder = ({
  checked,
  handleOrderCheckChange,
  item,
}: OrderHistoryCustomerItemProps) => {
  const { minDesktop, maxMobile } = useBreakpoint()
  const { t } = useLanguage(['extranet'])
  const { customer } = useCustomerContext()
  const navigate = useNavigate()
  const createLink = useExtranetPath()

  const getDosingCard = async (id: number[]) => {
    const ids = id
    const response = await api.orders.getDosingCardsForOrderIds(
      customer!.id,
      { ids },
      {
        format: 'blob',
      }
    )

    const blob = new Blob([response as unknown as string], {
      type: 'application/pdf',
    })

    window.open(window.URL.createObjectURL(blob), '_blank')
  }

  const getOrderChangesData = async (id: number[]) => {
    if (id && customer?.id) {
      const baseUrl = createLink('oma-annos.orders.orderChanges', {
        customerId: customer.id,
      })

      const url = id.reduce<string>((acc, curr) => {
        const isValidOrderId = item.id === curr && item.orderChangesFi.length > 0

        if (!isValidOrderId) return acc

        const firstParamExists = /\?o\=/.test(acc)

        return acc + (firstParamExists ? '&o=' : '?o=') + curr
      }, baseUrl)

      return navigate(url)
    }
  }

  return (
    <ExtranetTableRow
      noBorders
      noMargin
      style={{
        borderTop: minDesktop ? 'none' : '1px solid var(--color-accent-4)',
      }}
      expand={
        <>
          {item.orderChangesFi?.length > 0 && minDesktop && (
            <ExtranetContainer
              full
              flexColumn
              paddingLargeHorizontal
              paddingMediumVertical
            >
              <PharmacH6 paddingMediumBottom>{t('orderHistory.orderChanges')}: </PharmacH6>
              <PharmacColumn>
                {item.orderChangesFi.map((a) => (
                  <PharmacText
                    key={a}
                    paddingMediumBottom
                  >
                    {a}
                  </PharmacText>
                ))}
              </PharmacColumn>
            </ExtranetContainer>
          )}
          {!minDesktop && (
            <>
              <ExtranetContainer>
                <PharmacColumn
                  paddingMediumLeft
                  paddingMediumVertical
                >
                  <PharmacText paddingSmallBottom>{t('orderHistory.dateOfBirth')}</PharmacText>
                  <PharmacText
                    weight="bold"
                    paddingMediumBottom
                  >
                    {formatBirthdate(item.anonymizedSocialSecurityNumber)}
                  </PharmacText>
                  <PharmacText paddingSmallBottom>
                    {t('orderHistory.subscriptionPeriod')}
                  </PharmacText>
                  <PharmacText
                    weight="bold"
                    paddingMediumBottom
                  >
                    {formatDateString(item.startDate)}-{formatDateString(item.endDate)}
                  </PharmacText>
                  <PharmacText paddingSmallBottom>{t('orderHistory.orderNumber')}</PharmacText>
                  <PharmacText
                    weight="bold"
                    paddingMediumBottom
                  >
                    {item.id}
                  </PharmacText>
                  <PharmacText paddingSmallBottom>{t('orderHistory.orderState')}</PharmacText>
                  <PharmacText
                    weight="bold"
                    paddingMediumBottom
                  >
                    {t(`orderHistory.status.${item.status}`)}
                  </PharmacText>
                  <PharmacText paddingSmallBottom>{t('orderHistory.deliveryDate')}</PharmacText>
                  {item.status === 'sent' ? (
                    <PharmacText
                      weight="bold"
                      paddingMediumBottom
                    >
                      {formatDateString(item.sendingDate)}
                    </PharmacText>
                  ) : (
                    <PharmacText
                      weight="bold"
                      paddingMediumBottom
                    >
                      {t('orderHistory.status.missing-information')}
                    </PharmacText>
                  )}
                </PharmacColumn>
              </ExtranetContainer>
              {item.orderChangesFi?.length > 0 && (
                <ExtranetContainer
                  full
                  paddingLargeBottom
                  paddingMediumHorizontal
                >
                  <PharmacColumn>
                    <PharmacText paddingMediumBottom>
                      {t('orderHistory.orderChanges')}:{' '}
                    </PharmacText>
                    {item.orderChangesFi.map((a) => (
                      <PharmacText
                        key={a}
                        weight="semibold"
                        paddingLargeBottom
                      >
                        {a}
                      </PharmacText>
                    ))}
                  </PharmacColumn>
                </ExtranetContainer>
              )}
              <ExtranetContainer
                full
                alignCenter
                paddingLargeBottom
                flexColumn
              >
                <ExtranetContainer
                  full
                  alignCenter
                  justifyCenter
                  marginLargeLeft
                >
                  <PharmacButton
                    style={{ width: '13rem' }}
                    marginSmallLeft
                    onClick={() => getDosingCard([item.id])}
                    disabled={item.status !== 'sent'}
                    variant={item.status !== 'sent' ? 'secondary' : 'primary'}
                  >
                    <PharmacIcon
                      size="16px"
                      icon="externalLink"
                    />
                    <PharmacText
                      paddingSmallLeft
                      paddingSmallRight
                    >
                      {t('extranet:orderHistory.dosageCards')}
                    </PharmacText>
                  </PharmacButton>
                  <PharmacTooltip
                    maxWidth={maxMobile ? '300px' : '500px'}
                    paddingSmallLeft
                    handle={
                      <PharmacIcon
                        style={{ color: 'var(--color-info)' }}
                        size="30px"
                        icon="info"
                      />
                    }
                  >
                    <PharmacText>
                      {t('extranet:orderHistory.dosageCardButtonMobileTooltip')}
                    </PharmacText>
                  </PharmacTooltip>
                </ExtranetContainer>
                {item.orderChangesFi?.length > 0 && (
                  <ExtranetContainer
                    paddingMediumTop
                    full
                    alignCenter
                    justifyCenter
                    marginLargeLeft
                  >
                    <PharmacButton
                      style={{ width: '13rem' }}
                      marginSmallLeft
                      onClick={() => getOrderChangesData([item.id])}
                    >
                      <PharmacIcon
                        size="16px"
                        icon="externalLink"
                      />
                      <PharmacText
                        paddingSmallLeft
                        paddingSmallRight
                      >
                        {t('extranet:orderHistory.listOfChangesButton')}
                      </PharmacText>
                    </PharmacButton>
                    <PharmacTooltip
                      maxWidth={maxMobile ? '300px' : '500px'}
                      paddingSmallLeft
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="30px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>
                        {t('extranet:orderHistory.orderChangesButtonTooltip')}
                      </PharmacText>
                    </PharmacTooltip>
                  </ExtranetContainer>
                )}
              </ExtranetContainer>
            </>
          )}
        </>
      }
      toggleExpand={() => null}
      children={({ isExpanded }) => (
        <>
          {minDesktop && (
            <ExtranetTableColumn
              onClick={(e) => e.stopPropagation()}
              marginSmallLeft
              size={0.5}
            >
              <PharmacCheckbox
                checked={checked}
                handleChange={handleOrderCheckChange}
              />
            </ExtranetTableColumn>
          )}
          <ExtranetTableColumn size={2}>{item.wholeName}</ExtranetTableColumn>
          {minDesktop && (
            <ExtranetTableColumn size={1}>
              {formatBirthdate(item.anonymizedSocialSecurityNumber)}
            </ExtranetTableColumn>
          )}
          {minDesktop && (
            <ExtranetTableColumn size={1.5}>
              {formatDateString(item.startDate)}-{formatDateString(item.endDate)}
            </ExtranetTableColumn>
          )}
          {minDesktop && <ExtranetTableColumn>{item.id}</ExtranetTableColumn>}
          <ExtranetTableColumn style={{ overflow: 'visible' }}>
            <PharmacRowContainer>
              {item.isUrgent && (
                <>
                  <PharmacTooltip
                    position="right"
                    handle={
                      <PharmacIcon
                        size="20px"
                        icon="clock"
                      />
                    }
                  >
                    <PharmacText>{t('orderHistory.urgent')}</PharmacText>
                  </PharmacTooltip>
                  <div style={{ width: 'var(--spacing-medium)' }} />
                </>
              )}
              {item.orderChangesFi?.length > 0 && (
                <PharmacTooltip
                  position="right"
                  handle={
                    <PharmacIcon
                      size="20px"
                      icon="repeat"
                    />
                  }
                >
                  <PharmacText>{t('orderHistory.orderChange')}</PharmacText>
                </PharmacTooltip>
              )}
            </PharmacRowContainer>
          </ExtranetTableColumn>
          {minDesktop && (
            <ExtranetTableColumn>{t(`orderHistory.status.${item.status}`)}</ExtranetTableColumn>
          )}
          {minDesktop && (
            <ExtranetTableColumn>
              {item.status === 'sent'
                ? formatDateString(item.sendingDate)
                : t('orderHistory.status.missing-information')}
            </ExtranetTableColumn>
          )}
          <ExtranetExpandArrowColumn>
            {(item.orderChangesFi?.length > 0 && (
              <ExtranetCatalogItemChevron isExpanded={isExpanded} />
            )) ||
              (!minDesktop && <ExtranetCatalogItemChevron isExpanded={isExpanded} />)}
          </ExtranetExpandArrowColumn>
        </>
      )}
    />
  )
}
